import {observer} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';

import {AsyncPriceCourseCardContext} from '@udemy/browse-course';
import {
    UI_REGION,
    UpdateCareerGoalSelected,
    GoalCollectionTargetPage,
} from '@udemy/browse-event-tracking';
import {
    DiscoveryUnit,
    DiscoveryUnitItem,
    PAGE_TYPE_SUBS_LOGGED_IN_HOMEPAGE,
    PAGE_TYPE_SUBS_LOGGED_IN_HOMEPAGE_FIXED_UNITS,
} from '@udemy/discovery-api';
import {DeviceType} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {
    CourseUnit,
    DiscoveryUnitsContainer,
    DiscoveryUnitsContainerStore,
    SkillsHubCourseUnit,
} from '@udemy/react-discovery-units';
import {CourseCardSkeletonGroup} from '@udemy/react-reveal-components';

import {personalizeDiscoveryUnitPropData} from 'src/utils/personalize-course-unit-props-helper';
import {getTabTrackingContext} from 'src/utils/tab-tracking-helpers';

const FETCH_PAGE_SIZE = 3;

interface CertificationInterestDiscoveryUnitProps {
    enableCertificationBasedCourseUnitPP: boolean;
    enableNewCertificationBasedCourseUnitUIPP: boolean;
    deviceType: DeviceType;
    isMobile: boolean;
    className?: string;
    /** TODO: clean up once lihp_carousel_hover_popup_removal experiment concludes */
    showStateAwareCtaInCourseCard?: boolean;
}

const CertificationInterestDiscoveryUnitBase: React.FC<CertificationInterestDiscoveryUnitProps> = ({
    enableCertificationBasedCourseUnitPP,
    enableNewCertificationBasedCourseUnitUIPP,
    deviceType,
    isMobile,
    className = 'component-margin',
    showStateAwareCtaInCourseCard = false,
}) => {
    const {gettext} = useI18n();

    const [store] = useState(
        () =>
            new DiscoveryUnitsContainerStore({
                pageType: PAGE_TYPE_SUBS_LOGGED_IN_HOMEPAGE_FIXED_UNITS,
            }),
    );

    useEffect(() => {
        store.fetchUnits({pageSize: FETCH_PAGE_SIZE});
    }, [store]);

    if (!enableCertificationBasedCourseUnitPP || store.error) {
        return null;
    }

    const getUnitComponentAndProps = (
        unit: Partial<DiscoveryUnit<DiscoveryUnitItem>> | undefined,
    ) => {
        if (!unit) {
            throw new Error('Missing unit');
        }

        const responsiveUnit = {
            fullWidth: isMobile,
            showPager: !isMobile,
            layout: 'singlerow',
            gridCols: 4,
            showQuickViewBox: !showStateAwareCtaInCourseCard,
            showStateAwareCtaInCourseCard,
            ...personalizeDiscoveryUnitPropData(
                gettext('Edit certification interests'),
                '/personalize/certifications',
                new UpdateCareerGoalSelected(
                    GoalCollectionTargetPage.CERTIFICATIONS,
                    UI_REGION.COURSE_RECOMMENDATION_UNIT,
                ),
                false,
                false,
                UI_REGION.COURSE_RECOMMENDATION_UNIT,
            ),
        };

        // SkillsHubCourseUnit renders a tabbed interface driven by available_filters.units.
        // Only horizontal_tabbed units have that tab structure; default units do not.
        if (enableNewCertificationBasedCourseUnitUIPP && unit.view_type === 'horizontal_tabbed') {
            return {
                component: SkillsHubCourseUnit,
                componentProps: {
                    ...responsiveUnit,
                    compact: isMobile,
                    tabTrackingContext: getTabTrackingContext(UI_REGION.COURSE_RECOMMENDATION_UNIT),
                },
            };
        }

        return {
            component: CourseUnit,
            componentProps: {
                ...responsiveUnit,
                carouselProps: {
                    ariaLabel: unit.title,
                    ariaRoleDescription: 'carousel',
                },
            },
        };
    };

    return (
        <div
            aria-busy={store.firstLoad}
            className={className}
            data-purpose="certificate-interest-discovery-unit"
        >
            {store.firstLoad ? (
                <CourseCardSkeletonGroup withTitle={true} />
            ) : (
                <AsyncPriceCourseCardContext.Provider
                    value={{
                        useCourseProductCard: showStateAwareCtaInCourseCard,
                        hideCourseDescription: true,
                    }}
                >
                    <DiscoveryUnitsContainer
                        pageType={PAGE_TYPE_SUBS_LOGGED_IN_HOMEPAGE}
                        deviceType={deviceType}
                        units={store.units as DiscoveryUnit<DiscoveryUnitItem>[]}
                        store={store}
                        getUnitComponentAndProps={getUnitComponentAndProps}
                        filter={(u) => {
                            if (enableNewCertificationBasedCourseUnitUIPP) {
                                return (
                                    u.type === 'top' &&
                                    (u.view_type === 'horizontal_tabbed' ||
                                        u.view_type === 'default')
                                );
                            }
                            return u.view_type === 'certificate_interest_discovery_unit';
                        }}
                        disableInfiniteScroll={true}
                    />
                </AsyncPriceCourseCardContext.Provider>
            )}
        </div>
    );
};

export const CertificationInterestDiscoveryUnit = observer(CertificationInterestDiscoveryUnitBase);
